import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ChartBar, Broadcast } from "@phosphor-icons/react";
import api from "@/lib/api";

/**
 * Iter-45: Media buyer attribution dashboard.
 *
 * PII-masked view over (custom_landing_page, sub_id) → submitted / accepted
 * counts. Never returns names, phones, emails, or buyer margins. Media
 * buyers use this + the pixel manager to tune ad spend without holding
 * consumer PII.
 */
const WINDOWS = [7, 30, 90];

export default function MediaBuyerAttribution() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/media-buyer/attribution?days=${days}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e.response?.data?.detail || "Failed to load attribution"))
      .finally(() => setLoading(false));
  }, [days]);

  const totals = data?.totals || { submitted: 0, accepted: 0, recent_24h: 0, sandbox: 0 };
  const rate = useMemo(() => {
    if (!totals.submitted) return 0;
    return Math.round((totals.accepted / totals.submitted) * 100);
  }, [totals]);

  return (
    <div className="p-2" data-testid="media-buyer-attribution">
      <div className="flex items-center gap-3 mb-2 flex-wrap">
        <ChartBar size={22} weight="bold" />
        <h1 className="font-chivo font-black text-3xl tracking-tighter uppercase">
          Attribution
        </h1>
        <div className="label-mono ml-2">
          Last {days} days · aggregated by landing page + sub_id · no PII
        </div>
        <div className="ml-auto flex gap-1">
          {WINDOWS.map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              data-testid={`mb-window-${d}`}
              className={`border border-black px-3 py-1.5 text-[10px] uppercase tracking-widest ${
                days === d ? "bg-black text-white" : "hover:bg-zinc-100"
              }`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="brut-card p-4" data-testid="mb-total-submitted">
          <div className="label-mono">Submitted</div>
          <div className="font-chivo font-black text-3xl mt-1">{totals.submitted}</div>
        </div>
        <div className="brut-card p-4" data-testid="mb-total-accepted">
          <div className="label-mono">Accepted</div>
          <div className="font-chivo font-black text-3xl mt-1 text-emerald-800">
            {totals.accepted}
          </div>
        </div>
        <div className="brut-card p-4" data-testid="mb-total-rate">
          <div className="label-mono">Acceptance rate</div>
          <div className="font-chivo font-black text-3xl mt-1">{rate}%</div>
        </div>
        <div className="brut-card p-4 bg-black text-white" data-testid="mb-total-24h">
          <div className="label-mono opacity-70">Last 24 hours</div>
          <div className="font-chivo font-black text-3xl mt-1">{totals.recent_24h}</div>
        </div>
      </div>

      <div className="brut-card overflow-hidden" data-testid="mb-sources-card">
        <div className="border-b border-black px-4 py-3 flex items-center gap-2">
          <Broadcast size={14} weight="bold" />
          <div className="font-chivo font-black uppercase tracking-tighter">
            Landing page + sub_id breakdown
          </div>
          <div className="label-mono ml-auto">
            {(data?.sources || []).length} unique sources
          </div>
        </div>
        {loading ? (
          <div className="label-mono text-center py-10">Loading…</div>
        ) : !(data?.sources || []).length ? (
          <div className="label-mono text-center py-10 text-zinc-500">
            No traffic yet in this window.
          </div>
        ) : (
          <>
          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-xs" data-testid="mb-sources-table">
            <thead>
              <tr className="border-b-2 border-black bg-zinc-100 text-[10px] uppercase tracking-widest">
                <th className="text-left py-2 px-3">Firm / page</th>
                <th className="text-left py-2 px-3">sub_id</th>
                <th className="text-right py-2 px-3">Submitted</th>
                <th className="text-right py-2 px-3">Accepted</th>
                <th className="text-right py-2 px-3">Rate</th>
                <th className="text-right py-2 px-3">24h</th>
                <th className="text-right py-2 px-3">Sandbox</th>
              </tr>
            </thead>
            <tbody>
              {(data?.sources || []).map((s) => {
                const rateS = s.submitted
                  ? Math.round((s.accepted / s.submitted) * 100)
                  : 0;
                const key = `${s.custom_landing_page_id}-${s.sub_id}`;
                return (
                  <tr key={key} className="border-b border-zinc-200 hover:bg-zinc-50"
                      data-testid={`mb-src-row-${key}`}>
                    <td className="py-2 px-3 font-bold">{s.firm_name}</td>
                    <td className="py-2 px-3 font-mono text-[11px]">{s.sub_id}</td>
                    <td className="py-2 px-3 text-right font-mono">{s.submitted}</td>
                    <td className="py-2 px-3 text-right font-mono text-emerald-800">{s.accepted}</td>
                    <td className={
                      "py-2 px-3 text-right font-mono font-bold " +
                      (rateS >= 60 ? "text-emerald-700"
                        : rateS >= 30 ? "text-amber-700"
                          : "text-rose-700")
                    }>{rateS}%</td>
                    <td className="py-2 px-3 text-right font-mono">{s.recent_24h}</td>
                    <td className="py-2 px-3 text-right font-mono text-zinc-500">
                      {s.sandbox || "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
          {/* Mobile cards */}
          <div className="md:hidden p-2 flex flex-col gap-2" data-testid="mb-sources-cards">
            {(data?.sources || []).map((s) => {
              const rateS = s.submitted ? Math.round((s.accepted / s.submitted) * 100) : 0;
              const key = `${s.custom_landing_page_id}-${s.sub_id}`;
              return (
                <div key={key} className="border border-black bg-white p-3" data-testid={`mb-src-card-${key}`}>
                  <div className="flex items-center gap-2 mb-1 pb-1 border-b border-zinc-200">
                    <div className="font-bold text-sm truncate flex-1">{s.firm_name}</div>
                    <span className={
                      "text-[10px] font-mono font-bold px-1.5 py-0.5 border " +
                      (rateS >= 60 ? "border-emerald-700 text-emerald-700"
                        : rateS >= 30 ? "border-amber-700 text-amber-700"
                          : "border-rose-700 text-rose-700")
                    }>{rateS}%</span>
                  </div>
                  <div className="text-[11px] font-mono text-zinc-600 mt-1">sub_id: {s.sub_id}</div>
                  <div className="grid grid-cols-3 gap-x-3 text-xs font-mono mt-2">
                    <div><div className="label-mono">Submitted</div>{s.submitted}</div>
                    <div><div className="label-mono">Accepted</div><span className="text-emerald-800 font-bold">{s.accepted}</span></div>
                    <div><div className="label-mono">24h</div>{s.recent_24h}</div>
                  </div>
                </div>
              );
            })}
          </div>
          </>
        )}
      </div>

      <div className="mt-6 text-[11px] text-zinc-500 leading-relaxed max-w-3xl">
        <ChartBar size={11} weight="bold" className="inline mr-1" />
        This view is PII-safe by design — no names, phones, emails, buyer
        prices, or margin data. For per-lead debugging, contact your account
        admin.
      </div>
    </div>
  );
}
